import { SegmentType, Gender } from '@essent/common';
import type { Customer } from '@essent/customer';
import { getCustomerSuccess } from '@essent/customer';
import { emobilityActions } from '@innogy/emobility-shared';
import {
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
  type ProgressiveFormStateImplementor,
} from '@innogy/progressive-ngrx-forms';
import {
  getDateOffset,
  isDateInRange,
  isEmailAddress,
  isKvk,
  isLastName,
  isPhoneNumber,
  useValidatorIf,
  validateSequential,
  validateSequentialIf,
} from '@innogy/utils-deprecated';
import {
  EmobilityLeadSegmentType,
  type GetExternalLeadResponse,
} from '@integration/api-client/emobility';
import type { Action } from '@ngrx/store';
import { on } from '@ngrx/store';
import { setValue, updateGroup, validate } from 'ngrx-forms';
import { maxLength, notEqualTo, required } from 'ngrx-forms/validation';

import { initializeLeadForm } from './lead-form.actions';

export enum CustomerType {
  Household = 'Household',
  SelfEmployed = 'SelfEmployed',
  SME = 'SME',
  Other = 'Other',
}

export const CustomerTypeSegmentTypeMap = {
  [CustomerType.Household]: EmobilityLeadSegmentType.HH,
  [CustomerType.SelfEmployed]: EmobilityLeadSegmentType.SOHO,
  [CustomerType.SME]: undefined,
  [CustomerType.Other]: undefined,
} as const;

export const minimumDate = getDateOffset(0, 0, -100);
export const maximumDate = getDateOffset(0, 0, -18);

export interface CustomerDetailsFormState {
  customerType: `${CustomerType}`;
  salutation: `${Gender}` | undefined;
  initials: string;
  prefix: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  dateOfBirth: string;
  businessDetails: {
    organizationName: string;
    chamberOfCommerceNumber: string;
    emailAddress: string;
    phoneNumber: string;
  };
}

export interface CustomerDetailsState
  extends ProgressiveFormStateImplementor<CustomerDetailsFormState> {
  customer?: Customer;
}

export const customerDetailsFormId = 'emobilityLeadFormCustomerDetailsFormId';
export const customerTypeControlId = `${customerDetailsFormId}.customerType`;
export const customerEmailControlId = `${customerDetailsFormId}.emailAddress`;
export const customerBusinessEmailControlId = `${customerDetailsFormId}.businessDetails.emailAddress`;

export const customerDetailsFormState =
  createProgressiveFormGroupState<CustomerDetailsFormState>(
    customerDetailsFormId,
    {
      customerType: CustomerType.Household,
      salutation: undefined,
      initials: '',
      prefix: '',
      lastName: '',
      phoneNumber: '',
      dateOfBirth: '',
      emailAddress: '',
      businessDetails: {
        organizationName: '',
        chamberOfCommerceNumber: '',
        emailAddress: '',
        phoneNumber: '',
      },
    }
  );

export const initialCustomerDetailsState: CustomerDetailsState = {
  progressiveForm: customerDetailsFormState,
  customer: undefined,
};

function getCustomerTypeFromSegment(
  customerSegment: `${SegmentType}`
): CustomerType {
  return customerSegment === SegmentType.SOHO
    ? CustomerType.SelfEmployed
    : CustomerType.Household;
}

function shouldValidateUniqueEmail(
  customerType: `${CustomerType}`,
  customerSegment?: `${SegmentType}`
) {
  return (
    (customerSegment === SegmentType.HH &&
      customerType === CustomerType.SelfEmployed) ||
    (customerSegment === SegmentType.SOHO &&
      customerType === CustomerType.Household)
  );
}

function getEmail(customer: Customer, segmentType: SegmentType) {
  return customer.customerSegment === segmentType
    ? (customer.correspondence?.email ?? '')
    : '';
}

function getPhoneNumber(customer: Customer, segmentType: SegmentType) {
  return customer.customerSegment === segmentType
    ? (customer.correspondence?.phone?.phoneNumber ?? '')
    : '';
}

function getProspectEmailAddress(prospect: GetExternalLeadResponse) {
  return prospect?.businessDetails ? prospect.personalDetails.emailAddress : '';
}

function getProspectPhoneNumber(prospect: GetExternalLeadResponse) {
  return prospect?.businessDetails
    ? prospect.personalDetails?.phoneNumber || ''
    : '';
}

export const validateAndUpdateCustomerDetailsForm = (
  state: CustomerDetailsState
) => {
  const customerType = state.progressiveForm.formState.value.customerType;
  const isBusinessCustomer =
    customerType === CustomerType.SelfEmployed ||
    customerType === CustomerType.SME;
  const isHouseHold = customerType === CustomerType.Household;

  return updateGroup(state.progressiveForm.formState, {
    customerType: validate(required),
    salutation: validate(required),
    initials: validateSequential(required, maxLength(10)),
    lastName: validate(required, isLastName),
    dateOfBirth: validateSequentialIf(isHouseHold)(
      required,
      isDateInRange(minimumDate, maximumDate)
    ),
    phoneNumber: validateSequentialIf(isHouseHold)(required, isPhoneNumber),
    emailAddress: validateSequentialIf(isHouseHold)(
      required,
      isEmailAddress,
      useValidatorIf(
        notEqualTo(state.customer?.correspondence?.email),
        shouldValidateUniqueEmail(customerType, state.customer?.customerSegment)
      )
    ),

    businessDetails: updateGroup({
      organizationName: validateSequentialIf(isBusinessCustomer)(required),
      chamberOfCommerceNumber: validateSequentialIf(isBusinessCustomer)(
        required,
        isKvk
      ),
      phoneNumber: validateSequentialIf(isBusinessCustomer)(
        required,
        isPhoneNumber
      ),
      emailAddress: validateSequentialIf(isBusinessCustomer)(
        required,
        isEmailAddress,
        useValidatorIf(
          notEqualTo(state.customer?.correspondence?.email),
          shouldValidateUniqueEmail(
            customerType,
            state.customer?.customerSegment
          )
        )
      ),
    }),
  });
};

const _reducer = createProgressiveNgrxFormReducerWithFormStateUpdate(
  initialCustomerDetailsState,
  validateAndUpdateCustomerDetailsForm,
  on(getCustomerSuccess, (state, action) => ({
    ...state,
    customer: action.payload,
    progressiveForm: {
      ...state.progressiveForm,
      formState: setValue(state.progressiveForm.formState, {
        customerType: getCustomerTypeFromSegment(
          action.payload.customerSegment
        ),
        initials: action.payload.person?.initials ?? '',
        prefix: action.payload.person?.prefix ?? '',
        lastName: action.payload.person?.lastName ?? '',
        dateOfBirth: action.payload.person?.dateOfBirth ?? '',
        salutation: action.payload.person?.gender ?? undefined,
        emailAddress: getEmail(action.payload, SegmentType.HH),
        phoneNumber: getPhoneNumber(action.payload, SegmentType.HH),
        businessDetails: {
          organizationName: action.payload.organization?.organisationName ?? '',
          chamberOfCommerceNumber:
            action.payload.organization?.chamberOfCommerceNumber ?? '',
          emailAddress: getEmail(action.payload, SegmentType.SOHO),
          phoneNumber: getPhoneNumber(action.payload, SegmentType.SOHO),
        },
      }),
    },
  })),
  on(
    emobilityActions.getSalesPartnerProspectV1.successAction,
    // eslint-disable-next-line complexity
    (state, action) => ({
      ...state,
      progressiveForm: {
        ...state.progressiveForm,
        formState: setValue(state.progressiveForm.formState, {
          customerType: action.payload?.businessDetails
            ? CustomerType.SelfEmployed
            : CustomerType.Household,
          initials: action.payload.personalDetails?.initials ?? '',
          prefix: action.payload.personalDetails?.prefix ?? '',
          lastName: action.payload.personalDetails?.lastName ?? '',
          dateOfBirth: action.payload.personalDetails?.dateOfBirth ?? '',
          salutation: action.payload.personalDetails.gender ?? undefined,
          emailAddress: action.payload.personalDetails.emailAddress ?? '',
          phoneNumber: action.payload.personalDetails?.phoneNumber ?? '',
          businessDetails: {
            organizationName:
              action.payload?.businessDetails?.organizationName ?? '',
            chamberOfCommerceNumber:
              action.payload?.businessDetails?.chamberOfCommerceNumber ?? '',
            emailAddress: getProspectEmailAddress(action.payload),
            phoneNumber: getProspectPhoneNumber(action.payload),
          },
        }),
      },
    })
  ),
  on(initializeLeadForm, (state, action) => ({
    ...state,
    progressiveForm: {
      ...state.progressiveForm,
      formState: setValue(state.progressiveForm.formState, {
        ...state.progressiveForm.formState.value,
        customerType: action.payload.customerType,
        salutation: action.payload.salutationEnabled
          ? state.progressiveForm.formState.value.salutation
          : Gender.OTHER,
      }),
    },
  }))
);

export function customerDetailsReducer(
  state = initialCustomerDetailsState,
  action: Action
): CustomerDetailsState {
  return _reducer(state, action);
}
